import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import EventFeed from './components/EventFeed';
import EventFilters from './components/EventFilters';
import LiveStatistics from './components/LiveStatistics';
import AlertsPanel from './components/AlertsPanel';
import { twilioService } from '../../lib/twilioService';

const LiveOperationsFeed = () => {
  const { userProfile } = useAuth();
  const userRole = userProfile?.role || 'manager';
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filters, setFilters] = useState({
    eventTypes: [],
    dateRange: 'today',
    searchQuery: '',
    location: 'all'
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [smsAlertConfigs, setSmsAlertConfigs] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  // Mock real-time events data
  const mockEvents = [
    {
      id: 1,
      type: 'check-in',
      category: 'Member Activity',
      title: 'Member Check-In',
      description: 'Sarah Johnson checked in at Front Desk',
      memberName: 'Sarah Johnson',
      location: 'Main Entrance',
      timestamp: new Date(Date.now() - 2 * 60000)?.toISOString(),
      icon: 'UserCheck',
      color: 'success',
      priority: 'normal'
    },
    {
      id: 2,
      type: 'payment',
      category: 'Financial',
      title: 'Payment Processed',
      description: 'Monthly membership payment received',
      memberName: 'Michael Chen',
      amount: '$79.99',
      timestamp: new Date(Date.now() - 5 * 60000)?.toISOString(),
      icon: 'DollarSign',
      color: 'success',
      priority: 'normal'
    },
    {
      id: 3,
      type: 'class-booking',
      category: 'Class Activity',
      title: 'Class Booking',
      description: 'HIIT class booked for 6:00 PM',
      memberName: 'Emily Rodriguez',
      className: 'HIIT Bootcamp',
      timestamp: new Date(Date.now() - 8 * 60000)?.toISOString(),
      icon: 'Calendar',
      color: 'primary',
      priority: 'normal'
    },
    {
      id: 4,
      type: 'equipment-alert',
      category: 'Maintenance',
      title: 'Equipment Maintenance Alert',
      description: 'Treadmill #5 requires immediate inspection',
      equipment: 'Treadmill #5',
      timestamp: new Date(Date.now() - 12 * 60000)?.toISOString(),
      icon: 'AlertTriangle',
      color: 'warning',
      priority: 'high'
    },
    {
      id: 5,
      type: 'check-out',
      category: 'Member Activity',
      title: 'Member Check-Out',
      description: 'David Thompson checked out',
      memberName: 'David Thompson',
      location: 'Main Exit',
      timestamp: new Date(Date.now() - 15 * 60000)?.toISOString(),
      icon: 'UserMinus',
      color: 'primary',
      priority: 'normal'
    },
    {
      id: 6,
      type: 'class-cancellation',
      category: 'Class Activity',
      title: 'Class Cancellation',
      description: 'Yoga class cancelled by instructor',
      className: 'Morning Yoga',
      instructor: 'Lisa Martinez',
      timestamp: new Date(Date.now() - 18 * 60000)?.toISOString(),
      icon: 'XCircle',
      color: 'error',
      priority: 'high'
    },
    {
      id: 7,
      type: 'payment-failed',
      category: 'Financial',
      title: 'Payment Failed',
      description: 'Membership renewal payment declined',
      memberName: 'Robert Wilson',
      amount: '$149.99',
      timestamp: new Date(Date.now() - 22 * 60000)?.toISOString(),
      icon: 'CreditCard',
      color: 'error',
      priority: 'high'
    },
    {
      id: 8,
      type: 'staff-activity',
      category: 'Staff',
      title: 'Staff Clock-In',
      description: 'Trainer Marcus Thompson started shift',
      staffName: 'Marcus Thompson',
      timestamp: new Date(Date.now() - 25 * 60000)?.toISOString(),
      icon: 'Users',
      color: 'primary',
      priority: 'normal'
    },
    {
      id: 9,
      type: 'capacity-alert',
      category: 'Operations',
      title: 'Capacity Alert',
      description: 'Gym approaching maximum capacity (85%)',
      currentCapacity: '85%',
      timestamp: new Date(Date.now() - 30 * 60000)?.toISOString(),
      icon: 'AlertCircle',
      color: 'warning',
      priority: 'high'
    },
    {
      id: 10,
      type: 'check-in',
      category: 'Member Activity',
      title: 'Member Check-In',
      description: 'Jessica Brown checked in at Front Desk',
      memberName: 'Jessica Brown',
      location: 'Main Entrance',
      timestamp: new Date(Date.now() - 35 * 60000)?.toISOString(),
      icon: 'UserCheck',
      color: 'success',
      priority: 'normal'
    }
  ];

  useEffect(() => {
    if (userProfile?.gym_id) {
      loadAlertConfigurations();
    }
  }, [userProfile]);

  const loadAlertConfigurations = async () => {
    setLoadingAlerts(true);
    const { data, error } = await twilioService?.getAlertConfigurations(userProfile?.gym_id);
    if (!error && data) {
      setSmsAlertConfigs(data);
    }
    setLoadingAlerts(false);
  };

  const handleSendAlertSMS = async (event) => {
    if (event?.priority !== 'high') return;

    const alertData = {
      gymId: userProfile?.gym_id,
      alertType: event?.type,
      entityType: event?.category?.toLowerCase()?.replace(' ', '_'),
      entityId: event?.id,
      variables: {
        equipment_name: event?.equipment || 'Equipment',
        percentage: event?.currentCapacity || '0%',
        class_name: event?.className || 'Class',
        class_time: event?.time || 'scheduled time'
      }
    };

    const { data, error } = await twilioService?.sendFacilityAlert(alertData);
    
    if (!error) {
      setSuccessMessage('Alert SMS sent to staff members');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  };

  useEffect(() => {
    setEvents(mockEvents);
    setFilteredEvents(mockEvents);
  }, []);

  useEffect(() => {
    let filtered = [...events];

    // Filter by event types
    if (filters?.eventTypes?.length > 0) {
      filtered = filtered?.filter(event => filters?.eventTypes?.includes(event?.type));
    }

    // Filter by search query
    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(event => 
        event?.title?.toLowerCase()?.includes(query) ||
        event?.description?.toLowerCase()?.includes(query) ||
        event?.memberName?.toLowerCase()?.includes(query)
      );
    }

    // Filter by location
    if (filters?.location !== 'all') {
      filtered = filtered?.filter(event => event?.location === filters?.location);
    }

    setFilteredEvents(filtered);
  }, [filters, events]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLastUpdate(new Date());
      // In production, this would fetch new events from Supabase
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleRefresh = () => {
    setLastUpdate(new Date());
    // In production, fetch latest events from Supabase
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <div className="container mx-auto px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Live Operations Feed</h1>
            <p className="text-muted-foreground">Real-time monitoring of all gym activities and system events</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-success animate-pulse' : 'bg-muted'}`} />
              <span>Last update: {lastUpdate?.toLocaleTimeString()}</span>
            </div>
            <button
              onClick={handleRefresh}
              className="p-2 rounded-lg hover:bg-muted transition-colors focus-ring"
              title="Refresh feed"
            >
              <Icon name="RefreshCw" size={20} />
            </button>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg transition-colors focus-ring ${
                autoRefresh 
                  ? 'bg-success/10 text-success hover:bg-success/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name={autoRefresh ? 'Pause' : 'Play'} size={16} />
                <span className="text-sm font-medium">{autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}</span>
              </div>
            </button>
          </div>
        </div>

        {/* Live Statistics */}
        <LiveStatistics events={events} />

        {/* Alerts Panel */}
        <AlertsPanel 
          events={events?.filter(e => e?.priority === 'high')} 
          onSendSMS={handleSendAlertSMS}
          smsEnabled={true}
        />

        {/* SMS Alert Configuration Button */}
        {(userRole === 'manager' || userRole === 'owner' || userRole === 'sysadmin') && (
          <div className="mt-6">
            <button
              onClick={() => setShowAlertModal(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors focus-ring flex items-center gap-2"
            >
              <Icon name="Settings" size={18} />
              <span>Manage SMS Alert Configurations</span>
            </button>
          </div>
        )}

        {/* Success Message */}
        {showSuccessMessage && (
          <div className="fixed top-20 right-6 z-50 bg-success text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
            <Icon name="CheckCircle" size={20} />
            <span>{successMessage}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <EventFilters 
              filters={filters} 
              onFilterChange={handleFilterChange}
              eventCount={filteredEvents?.length}
            />
          </div>

          {/* Event Feed */}
          <div className="lg:col-span-3">
            <EventFeed events={filteredEvents} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveOperationsFeed;